import classNames from 'classnames'
import * as React from 'react'

import type {ProjectId} from '../../../../types'
import {stringifyId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import type {Child} from '../../SequenceLoader/SequenceLoader'
import SequenceLoader, {SequenceLoaderChild} from '../../SequenceLoader/SequenceLoader'

import {ArchivedSubprojectsToggle} from './ArchivedSubprojectsToggle/ArchivedSubprojectsToggle'
import Subproject from './Subproject/Subproject.container'

import styles from './Subprojects.css'

type DefaultProps = {
  level: number
  onlyFavorites: boolean
  projectsReady: boolean
}
type Props = DefaultProps & {
  notArchivedProjectIds?: ProjectId[]
  favoriteProjectIds?: ProjectId[]
  limitWidth?: boolean
  projectId: ProjectId
}

type NotArchivedSubprojectsProps = {
  seqKey?: string
  notArchivedProjectIds?: ProjectId[]
  favoriteProjectIds?: ProjectId[]
  onlyFavorites: boolean
  level: number
}

function NotArchivedSubprojects({
  notArchivedProjectIds,
  favoriteProjectIds = [],
  onlyFavorites,
  level,
}: NotArchivedSubprojectsProps) {
  const subprojects = React.useMemo(() => {
    if (onlyFavorites) {
      return favoriteProjectIds
    }

    if (notArchivedProjectIds?.length) {
      const favorites = notArchivedProjectIds.filter(id => favoriteProjectIds.includes(id))
      const nonFavorites = notArchivedProjectIds.filter(id => !favoriteProjectIds.includes(id))
      return [...favorites, ...nonFavorites]
    }

    return emptyArray
  }, [favoriteProjectIds, notArchivedProjectIds, onlyFavorites])

  const renderSubproject = (id: ProjectId) => (
    <Subproject
      seqKey={stringifyId(id)}
      level={level}
      onlyFavorites={onlyFavorites}
      key={stringifyId(id)}
      projectId={id}
    />
  )

  return (
    <SequenceLoader showFirstLoadingItem withIncrementalRendering>
      {subprojects.map<any>(renderSubproject)}
    </SequenceLoader>
  )
}

class Subprojects extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    level: 0,
    onlyFavorites: false,
    projectsReady: false,
  }

  renderNotArchivedSubprojects(): Child {
    const {notArchivedProjectIds, favoriteProjectIds, onlyFavorites, level} = this.props
    return (
      <NotArchivedSubprojects
        seqKey="notArchived"
        notArchivedProjectIds={notArchivedProjectIds}
        favoriteProjectIds={favoriteProjectIds}
        onlyFavorites={onlyFavorites}
        level={level}
      />
    )
  }

  render(): React.ReactNode {
    const {projectsReady, level, onlyFavorites, limitWidth, projectId} = this.props

    const nested = level > 0
    return projectsReady ? (
      <div
        className={classNames({
          [styles.root]: !nested && !onlyFavorites,
          [styles.limitWidth]: limitWidth,
        })}
      >
        <SequenceLoader showFirstLoadingItem>
          {this.renderNotArchivedSubprojects()}
          {!nested && !onlyFavorites ? (
            <SequenceLoaderChild seqKey="archived">
              <ArchivedSubprojectsToggle projectId={projectId} />
            </SequenceLoaderChild>
          ) : null}
        </SequenceLoader>
      </div>
    ) : null
  }
}

export default Subprojects
